// src/components/ArticleOverlay.jsx
import React from 'react';
import { X, Calendar, Clock, Link2, ExternalLink, ArrowUpRight } from 'lucide-react';
import { extractLinks, prettifyUrl, renderTextWithLinks } from '../utils/linkUtils';

const ArticleOverlay = ({ article, onClose }) => {
    if (!article) return null;

    const formattedDate = article.dateLabel || new Date(article.createdAt).toLocaleDateString(undefined, {
        timeZone: 'America/Antigua', year: 'numeric',
        month: 'long',
        day: 'numeric',
    });

    const links = extractLinks(article.content);

    return (
        <div className="fixed inset-0 z-50">
            {/* Backdrop */}
            <div
                className="fixed inset-0 bg-purple-950/70 backdrop-blur-sm"
                onClick={onClose}
            />

            {/* Content */}
            <div className="relative flex min-h-screen items-center justify-center p-4 sm:p-6">
                <div
                    className="relative flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden
                               rounded-2xl bg-white shadow-2xl animate-fade-in-up"
                    onClick={e => e.stopPropagation()}
                >
                    {/* Close button — pinned to the card, stays put while the body scrolls */}
                    <button
                        onClick={onClose}
                        aria-label="Close article"
                        className="absolute right-4 top-4 z-20 grid h-10 w-10 place-items-center rounded-full
                                 bg-white/90 text-gray-700 shadow-lg backdrop-blur-sm transition-all duration-300
                                 hover:rotate-90 hover:bg-white"
                    >
                        <X className="w-5 h-5" />
                    </button>

                    {/* Scrollable content area */}
                    <div className="overflow-y-auto">
                    {/* Header band — title sits on its own, never cropping the image */}
                    <div className="px-6 pt-8 pb-6 sm:px-10 sm:pt-10 border-b border-gray-100">
                        <span className="inline-block mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-purple-600">
                            Latest News
                        </span>
                        <h1 className="font-display text-3xl sm:text-4xl font-semibold text-gray-900 leading-tight">
                            {article.title}
                        </h1>
                        <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-gray-500">
                            <div className="flex items-center">
                                <Calendar className="w-4 h-4 mr-1.5 text-purple-500" />
                                {formattedDate}
                            </div>
                            <div className="flex items-center">
                                <Clock className="w-4 h-4 mr-1.5 text-purple-500" />
                                5 min read
                            </div>
                        </div>
                    </div>

                    {/* Image — shown IN FULL via blurred letterbox backdrop + object-contain */}
                    {article.imageUrl && (
                        <div className="relative w-full bg-gray-900 overflow-hidden">
                            {/* Blurred fill so any aspect ratio looks intentional */}
                            <img
                                src={article.imageUrl}
                                alt=""
                                aria-hidden="true"
                                className="absolute inset-0 w-full h-full object-cover scale-110 blur-2xl opacity-50"
                            />
                            {/* Actual image, never cropped */}
                            <img
                                src={article.imageUrl}
                                alt={article.title}
                                className="relative mx-auto max-h-[65vh] w-auto max-w-full object-contain"
                            />
                        </div>
                    )}

                    {/* Article body */}
                    <div className="px-6 py-8 sm:px-10 sm:py-10">
                        <div className="prose prose-lg max-w-none prose-p:text-gray-600 prose-p:leading-relaxed">
                            {article.content.split('\n').map((paragraph, index) => (
                                paragraph.trim() ? (
                                    <p key={index} className="mb-4 text-gray-600 leading-relaxed">
                                        {renderTextWithLinks(paragraph)}
                                    </p>
                                ) : null
                            ))}
                        </div>

                        {/* Links & Resources — surfaces every pasted URL as a clear call-to-action */}
                        {links.length > 0 && (
                            <div className="mt-8 rounded-2xl border border-purple-100 bg-purple-50/60 p-5 sm:p-6">
                                <h3 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-purple-700">
                                    <Link2 className="h-4 w-4" />
                                    Links &amp; Resources
                                </h3>
                                <div className="mt-4 space-y-2.5">
                                    {links.map((link, i) => (
                                        <a
                                            key={i}
                                            href={link}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="group flex items-center justify-between gap-3 rounded-xl border border-purple-100
                                                     bg-white px-4 py-3 shadow-sm transition-all hover:-translate-y-0.5
                                                     hover:border-purple-300 hover:shadow-md"
                                        >
                                            <span className="flex min-w-0 items-center gap-3">
                                                <span className="grid h-9 w-9 flex-shrink-0 place-items-center rounded-lg bg-purple-100 text-purple-600">
                                                    <ExternalLink className="h-4 w-4" />
                                                </span>
                                                <span className="truncate text-sm font-semibold text-gray-800 group-hover:text-purple-700">
                                                    {prettifyUrl(link)}
                                                </span>
                                            </span>
                                            <ArrowUpRight className="h-5 w-5 flex-shrink-0 text-purple-400 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-purple-600" />
                                        </a>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ArticleOverlay;
